import { css } from '@emotion/react';
import { useEffect, useState } from 'react';

import NvIcon from 'shared/components/nv-icon';
import NvGeneratingLoader from 'shared/components/loaders/nv-generating-loader';

const FADE_DURATION = 300; // ms
const STATUS_INTERVAL = 2000; // ms
const TICK_DELAY = 1000; // ms - delay before showing tick and dwell time before starting fade-out
const LOADING_DOTS = '...';

const styles = css`
  min-height: 300px;
  .lh-25 {
    line-height: 25px;
  }
  .status-container {
    height: 25px;
  }
  .status-text-wrapper {
    left: 0;
    right: 0;
    transition: opacity ${FADE_DURATION}ms ease-in-out;
    &.fade-out {
      opacity: 0;
    }
    &.fade-in {
      opacity: 1;
    }
  }
`;

type AiGenerationPreviewProps = {
  aiGenerationTexts: string[];
  showTick?: boolean;
};

const AiGenerationPreview = (props: AiGenerationPreviewProps) => {
  const { aiGenerationTexts = [], showTick = false } = props;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFading, setIsFading] = useState(false);
  const [showTickIcon, setShowTickIcon] = useState(false);

  const isLastItem = currentIndex >= aiGenerationTexts.length - 1;

  useEffect(() => {
    if (!aiGenerationTexts.length) return undefined;

    let tickTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let fadeTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let nextItemTimeoutId: ReturnType<typeof setTimeout> | null = null;

    const runCycle = () => {
      if (showTick) {
        // Show tick after TICK_DELAY
        tickTimeoutId = setTimeout(() => {
          setShowTickIcon(true);

          if (isLastItem) return;

          // Start fade out after another TICK_DELAY
          fadeTimeoutId = setTimeout(() => {
            setIsFading(true);

            // After fade out completes, change text and fade in
            nextItemTimeoutId = setTimeout(() => {
              setShowTickIcon(false); // Reset tick after fade completes
              setCurrentIndex((prevIndex) => {
                if (prevIndex >= aiGenerationTexts.length - 1) {
                  return prevIndex;
                }
                return prevIndex + 1;
              });
              setIsFading(false);
            }, FADE_DURATION);
          }, TICK_DELAY);
        }, TICK_DELAY);
      } else if (!isLastItem) {
        // Original behavior without tick
        fadeTimeoutId = setTimeout(() => {
          setIsFading(true);

          nextItemTimeoutId = setTimeout(() => {
            setCurrentIndex((prevIndex) => {
              if (prevIndex >= aiGenerationTexts.length - 1) {
                return prevIndex;
              }
              return prevIndex + 1;
            });
            setIsFading(false);
          }, FADE_DURATION);
        }, STATUS_INTERVAL - FADE_DURATION);
      }
    };

    runCycle();

    return () => {
      if (tickTimeoutId) clearTimeout(tickTimeoutId);
      if (fadeTimeoutId) clearTimeout(fadeTimeoutId);
      if (nextItemTimeoutId) clearTimeout(nextItemTimeoutId);
    };
  }, [currentIndex, aiGenerationTexts, isLastItem, showTick]);

  return (
    <div className='d-flex flex-column align-items-center pt-6 pb-4' css={styles}>
      <NvGeneratingLoader className='mb-0 mt-4' />
      <div className='status-container w-100 text-center position-relative mt-2'>
        <div className={`status-text-wrapper d-flex justify-content-center align-items-center position-absolute ${isFading ? 'fade-out' : 'fade-in'}`}>
          <p className='text-regular text-gray-2 lh-25 mb-0'>
            {aiGenerationTexts[currentIndex] ?? ''}
          </p>
          {showTick && (
            showTickIcon ? (
              <NvIcon
                icon='check'
                size='xss-smallest'
                className='text-kelp ml-1 lh-25'
              />
            ) : (
              <span className='text-regular text-gray-2 lh-25'>{LOADING_DOTS}</span>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default AiGenerationPreview;
